import { LOCALE } from '@hhgtech/hhg-components/types'
import dayjs from 'dayjs'
import innertext from 'innertext'

import { CARE_PATH_DEFAULT_COLOR_PATTERN } from 'components/templates/carePath/constant'
import {
  API_DATE_FORMAT,
  basePath,
  DEFAULT_IMG,
  POST_DATE_FORMAT,
  TRANSLATE_LANGUAGES,
} from 'interfaces/constants'
import {
  AdditionalQuestionFieldType,
  AdditionalQuestionFieldTypeApi,
  AffiliateChannel,
  AffiliateCurrency,
  AffiliateProduct,
  AffiliateProductApi,
  AffiliateProductCategory,
  AffiliateProductCategoryApi,
  Article,
  ArticleApi,
  ArticleExpert,
  ArticleExpertApi,
  ArticleReaction,
  ArticleReactionApi,
  Author,
  AuthorApi,
  BabyGrowthContent,
  BabyGrowthContentApi,
  BabyGrowthResult,
  BabyGrowthResultApi,
  BabyPoopPage,
  BabyPoopPageApi,
  BabyVaccinationBanner,
  BabyVaccinationBannerApi,
  BabyVaccinationMainTimelines,
  BabyVaccinationMainTimelinesApi,
  BabyVaccinationRecord,
  BabyVaccinationRecordsApi,
  BabyVaccinationStatus,
  BabyVaccinationVaccineInfo,
  BabyVaccinationVaccineInfoApi,
  BMICondition,
  BMIResult,
  BMIResultApi,
  Breadcrumb,
  BreadcrumbApi,
  CareDoctor,
  CareDoctorInfoApi,
  CareHospital,
  CareHospitalInfoApi,
  CarePathGroup,
  CarePathGroupApi,
  CareService,
  CareServiceInfoApi,
  CareSpecialties,
  Category,
  CategoryApi,
  CategoryHealthToolApi,
  CategoryMeta,
  CategoryMetaApi,
  CollectingDataForm,
  CollectingDataFormApi,
  CollectingDataFormType,
  AdditionalInfo,
  AdditionalInfoApi,
  CommonHealthtoolInfo,
  CommonHealthtoolInfoApi,
  CommunityEmbed,
  CommunityEmbedApi,
  DefaultCarousel,
  DefaultCarouselApi,
  DoctorSchedule,
  DoctorScheduleData,
  DoctorScheduleDataApi,
  Expert,
  ExpertApi,
  ExpertUser,
  ExpertUserApi,
  ExpertUserSummary,
  ExpertUserSummaryApi,
  FAQs,
  FAQsApi,
  HealthTool,
  HealthToolApi,
  HealthToolByCategory,
  HomepageBannerType,
  HomepageBannerTypeApi,
  InfoBabyVaccineResultPage,
  InfoBabyVaccineResultPageApi,
  Integrates3rdPartyApiType,
  Integrates3rdPartyType,
  MedicalReview,
  MedicalReviewApi,
  MetaTag,
  MetaTagApi,
  PageCategory,
  PageCategoryApi,
  PagingRecentPost,
  PagingRecentPostApi,
  PwgInfo,
  PwgInfoApi,
  PWGResult,
  PwgResult,
  PWGResultApi,
  RecentPost,
  RecentPostApi,
  Result,
  ResultApi,
  ReviewedCategory,
  ReviewedCategoryApi,
  ReviewedUserApi,
  Role,
  ScheduleApi,
  ServiceScheduleData,
  ServiceScheduleDataApi,
  SingleSubot,
  SingleSubotApi,
  SiteWideBanner,
  SiteWideBannerApi,
  Sponsor,
  SponsorApi,
  Spotlight,
  SpotlightApi,
  SpotlightExpert,
  SpotlightExpertApi,
  SpotlightLandingPageBanner,
  SpotlightLandingPageBannerApi,
  SpotlightPageBanner,
  SpotlightPageBannerApi,
  SpotlightSummary,
  SpotlightSummaryApi,
  SpotlightSuperContentBanner,
  SpotlightSuperContentBannerApi,
  SpotlightTemplate,
  SpotlightVideos,
  StaticPage,
  StaticPageApi,
  StatisticCarousel,
  StatisticCarouselApi,
  SubscriptionBanner,
  SubscriptionBannerApi,
  ThrInfo,
  ThrInfoApi,
  THRResult,
  THRResultApi,
  TogetherComment,
  TogetherCommentApi,
  // VaccineApi,
  // Vaccine,
  TogetherCommunity,
  TogetherCommunityApi,
  TogetherPost,
  TogetherPostApi,
  TogetherTopic,
  TogetherTopicApi,
  ToolContent,
  ToolContentApi,
  WeekOfPregnancy,
  WeekOfPregnancyApi,
  KeyPillarsArticlesTypeApi,
  KeyPillarsArticlesType,
  Frequency,
} from 'interfaces/types'
import {
  AgeGroup,
  AgeGroupApi,
  BabyVaccinationContent,
  BabyVaccinationContentApi,
  BabyVaccinationResult,
  BabyVaccinationResultApi,
} from 'interfaces/types/BabyVaccination'
import { Specialty, SpecialtyApi } from 'interfaces/types/Specialty'
import { PhoneVerifyType } from 'state/types'

import { formatSpotlightSession } from './carePath'
import { wrapFormatFn } from './utils'
import { formatVideoItem } from './videosPage'
import {
  OvulationInfo,
  OvulationInfoApi,
  OvulationSuggestedArticle,
  OvulationSuggestedArticleApi,
} from '../../interfaces/types'

export * from './utils'

export const formatAuthor = wrapFormatFn(
  (author?: AuthorApi): Author => {
    if (!author) return {} as Author

    return {
      displayName: author.display_name || null,
      avatar: author.avatar || null,
      thumbnailAvatar: author.avatar_thumbnail || null,
      profileUrl: author.profile_url || null,
      description: author.description || null,
      metaTag: author.meta_tag || '',
      slug: author?.slug || '',
      role: author?.role || '',
      specialty: author?.specialty || '',
    } as Author
  },
)
export const formatArticleExpert = wrapFormatFn(
  (expert: ArticleExpertApi): ArticleExpert => {
    if (!expert) return {} as ArticleExpert

    return {
      id: expert.user_id || null,
      name: expert.name || null,
      specialty: expert.specialty || null,
      avatar: expert.avatar || null,
      permalink: expert.permalink || null,
      thumbnailAvatar: expert.avatar_thumbnail || null,
    } as ArticleExpert
  },
)
export const formatSponsor = wrapFormatFn(
  (sponsor: SponsorApi): Sponsor => {
    if (!sponsor) return {} as Sponsor

    return {
      id: sponsor.id || '',
      title: sponsor.title,
      logo: sponsor.logo,
      link: sponsor.link,
      displayName: sponsor.displaying_name || '',
    } as Sponsor
  },
)

export const formatArticleFaqs = wrapFormatFn(
  (faq: { question: string; answer: string }): FAQs => {
    if (!faq) return {} as FAQs

    return {
      title: faq.question,
      content: faq.answer,
    } as FAQs
  },
)

export const formatTranslateOptions = wrapFormatFn(
  (translate?: {
    [key: string]: string
  }): { value: string; label: string }[] => {
    if (typeof translate === 'undefined' || Object.keys(translate).length < 2)
      return []

    return Object.keys(translate).map((languageKey) => ({
      label:
        languageKey.toUpperCase() in TRANSLATE_LANGUAGES
          ? TRANSLATE_LANGUAGES[languageKey.toUpperCase() as 'EN' | 'FIL']
          : languageKey.toUpperCase(),
      value:
        translate[languageKey].slice(-1) === '/'
          ? translate[languageKey]
          : `${translate[languageKey]}/`,
    }))
  },
)

export const formatBreadcrumb = wrapFormatFn(
  (breadcrumb?: BreadcrumbApi): Breadcrumb => {
    if (!breadcrumb) return {} as Breadcrumb

    return {
      catName: breadcrumb.cat_name,
      image: breadcrumb.image,
      name: breadcrumb.name,
      permalink: breadcrumb.permalink,
      termId: breadcrumb.term_id,
      enName: breadcrumb.en_name,
      icon: breadcrumb.icon,
      imageColor: breadcrumb.image_color,
      textColor: breadcrumb.text_color,
    }
  },
)
const formatArticleSubscriptionBanner = wrapFormatFn(
  (banner?: SubscriptionBannerApi): SubscriptionBanner => {
    if (!banner) return {} as SubscriptionBanner
    const {
      button_text,
      button_url,
      status,
      banner_desktop,
      banner_mobile,
      ...rest
    } = banner
    return {
      buttonText: button_text || '',
      buttonUrl: button_url || '',
      status: Boolean(status),
      bannerDesktop: banner_desktop || '',
      bannerMobile: banner_mobile || '',
      ...rest,
    }
  },
)
export const formatArticle = wrapFormatFn(
  (article: ArticleApi): Article => {
    if (!article) return {} as Article

    return {
      ...article,
      reviewed: article.reviewed,
      id: article.ID,
      postName: article.post_name,
      sponsor: article.sponsor ? formatSponsor(article.sponsor) : null,
      isSponsored: article.is_sponsored || null,
      isPremium: article.is_premium || false,
      postTitle: article.post_title || '',
      postModified: article.post_modified || '',
      postDate: article.post_date || '',
      modifiedTime: article.modified_time || '',
      permalink: article.permalink || '',
      permalinkDecode: article.permalink_decode || '',
      featureImage: article.image || null,
      featureImageMobile: article.image_medium || null,
      featureImageMobileXs: article.image_thumbnail || null,
      featureImageWithRatio1x1:
        article.feature_1x1 || article.image || DEFAULT_IMG,
      author: formatAuthor(article.author),
      reviewer: article.reviewed_by
        ? formatAuthor(article.reviewed_by) || null
        : null,
      articleReviewed: article?.reviewed
        ? {
            ...article?.reviewed,
            author: formatUserProfile(article?.reviewed?.author),
            editor: formatUserProfile(article?.reviewed?.editor),
            expert: formatUserProfile(article?.reviewed?.expert),
            fact_check: formatUserProfile(article?.reviewed?.fact_check),
          }
        : null,
      category: formatCategory(article.category),
      postContent: article.post_content || null,
      keyTakeaways: article.key_takeaways || null,
      categories: (article.categories || []).map(formatCategory),
      sources: article.sources || null,
      description: article.description || null,
      excerpt: article.excerpt || null,
      expert: formatArticleExpert(article.expert),
      metaTag: article.meta_tag || null,
      spotlight: formatSpotlightsSummary(article?.spotlight),
      embedSpotlights:
        article?.embeb_spotlight?.map(formatSpotlightsSummary) || [],
      affiliateMessage: article.affiliate_message || null,
      turnOffComment: !!article.turn_off_comment,
      hasAffiliate:
        !!article.is_affiliate ||
        article.label?.includes('product_review') ||
        false,
      faqs: (article.faqs || []).map(formatArticleFaqs),
      translateOptions: formatTranslateOptions(article.translate),
      jumpingLink: article.jumping_link || [],
      pregnancy: article?.pregnancy || false,
      pregnancyWeek: article?.week_pregnancy || 1,
      primaryCategory: formatCategory(article.primary_category),
      turnOffIvsPlayer: !!article.turn_off_ivs_player,
      turnOffArticleVoice: !!article.turn_off_article_voice,
      enableAudio: !!article.enable_audio,
      breadcrumb: (article.breadcrumb || [])?.map(formatBreadcrumb) || [],
      label: article.video?.video_url ? 'video_content' : article.label || '',
      editLast: formatAuthor(article.edit_last),
      subscriptionBanner: formatArticleSubscriptionBanner(
        article.subscription_banner,
      ),
      integrates3rdParty: formatThirdPartyList(
        article?.['integrates-3rd-party'],
      ),
      unifiedProfileSubot: {
        id: article?.unified_profile_subot?.id || '',
        class: article?.unified_profile_subot?.class || '',
      },
      appDownloadEmbedded: {
        banner: {
          desktop: article?.app_download_embedded?.banner?.desktop || '',
          mobile: article?.app_download_embedded?.banner?.mobile || '',
        },
        url: article?.app_download_embedded?.url_app_install || '',
        class: article?.app_download_embedded?.class || '',
      },
      video: {
        ...formatVideoItem(article?.video),
        relatedVideo: (article?.video?.related_video || []).map(
          formatVideoItem,
        ),
      },
      adsOnArticles: {
        ads_on_articles_for_desktop: !!article?.ads_on_articles_for_desktop,
        ads_on_articles_for_mobile: !!article?.ads_on_articles_for_mobile,
      },
      adsExcludeSponsored: {
        ads_exclude_sponsored_desktop: !!article?.ads_exclude_sponsored_desktop,
        ads_exclude_sponsored_mobile: !!article?.ads_exclude_sponsored_mobile,
      },
      isParentingOrPregnancy:
        typeof article.is_parenting_or_pregnancy == 'boolean'
          ? article.is_parenting_or_pregnancy
          : false,
      dictionaryTableContent: (article.dictionary_table_content || []).map(
        (i) => ({
          h2Title: i.h2_title || '',
          h2Id: i.h2_id || '',
          h3SubH2: (i.h3_sub_h2 || []).map((j) => ({
            h3Title: j.h3_title || '',
            h3Id: j.h3_id || '',
          })),
        }),
      ),
      featuredImageAfterFirstParagraph: !!article.featured_image_after_first_paragraph,
    } as Article
  },
)

export const formatKeyPillarArticles = wrapFormatFn(
  (data: KeyPillarsArticlesTypeApi): KeyPillarsArticlesType => {
    if (!data) return {} as KeyPillarsArticlesType

    return {
      articles: (data.articles || []).map(formatArticle),
      keyPillars: {
        name: data.key_pillars?.name || '',
        slug: data.key_pillars?.slug || '',
      },
    }
  },
)

export const formatUserProfileUrl = (
  user?: ReviewedUserApi | ExpertUserSummaryApi | Author | null,
): string => {
  if (typeof user === 'undefined' || !user || !user?.role) return ''

  return user?.turn_off_profile
    ? ''
    : user?.slug
    ? ['expert', 'medical'].includes(user?.role || '')
      ? `/expert/${user?.slug}`
      : `/author/${user?.slug}`
    : user?.profile_url || user?.permalink || '#'
}

export const formatUserProfile = wrapFormatFn(
  (
    user: ReviewedUserApi | null | undefined,
  ): ReviewedUserApi | null | undefined => {
    if (typeof user === 'undefined' || !user) return null
    return {
      ...user,
      profile_url: formatUserProfileUrl(user),
      ...(user.name && { name: innertext(user.name) }),
      ...(user.display_name && { display_name: innertext(user.display_name) }),
    }
  },
)

export const formatCategory = wrapFormatFn(
  (category: CategoryApi): Category => {
    if (!category) return {} as Category

    return {
      termId: category.term_id || null,
      sponsor: category.sponsor ? formatSponsor(category.sponsor) : null,
      isSponsored: category.is_sponsored || null,
      categoryName: category.cat_name || null,
      name: category.name || null,
      iconUrl: category.icon || '',
      slugName: category.category_nicename || null,
      description: category.description || null,
      featureImage: category.image || null,
      displayName: category.name || null,
      parent: category.parent || null,
      permalink: category.permalink || null,
      permalinkDecode: category.permalink_decode || '',
      slug: category.slug || null,
      childCates:
        category.child_cates?.map((childCate) => {
          return formatCategory(childCate)
        }) || [],

      colorFeatureImage: category.color_feature_image || null,
      imageColor: category.image_color || null,
      textColor: category.text_color || null,
      catParent: category.cat_parent?.name
        ? {
            name: category.cat_parent.name,
            permalink: category.cat_parent.permalink,
          }
        : {},
      metaTag: category.meta_tag || '',
      reviewedBy: category.reviewed_by
        ? formatReviewedCategory(category.reviewed_by)
        : null,
      reviewedDate: category.reviewed_date || '',
      faqs: category.faqs
        ? category.faqs.map((faq) => formatFaqsCategory(faq))
        : null,
      translateOptions: formatTranslateOptions(category?.translate),
      suggestionTools: category.suggestion_tools
        ? category.suggestion_tools.map((tool) => ({
            ...formatHealthTool(tool),
            reviewedBy: tool?.reviewed_by
              ? ((formatReviewedCategory(
                  (tool?.reviewed_by as unknown) as ReviewedCategoryApi,
                ) as unknown) as Author)
              : null,
            reviewedDate: tool?.reviewed_date || '',
          }))
        : [],
      suggestionSubots: category.suggestion_subots
        ? {
            title: category.suggestion_subots?.title_riskscreener || '',
            subots:
              category.suggestion_subots?.subots?.map(formatHealthTool) || [],
          }
        : null,
      customizedUrl: category.customized_url || null,
      engName: category.en_name || null,
      sponsorInInfinity: category.sponsor_in_infinity
        ? formatSponsor(category.sponsor_in_infinity)
        : null,
      adsOnArticles: {
        ads_on_articles_for_desktop: !!category?.ads_on_articles_for_desktop,
        ads_on_articles_for_mobile: !!category?.ads_on_articles_for_mobile,
      },
    } as Category
  },
)

export const formatCommunityEmbed = wrapFormatFn(
  (community?: CommunityEmbedApi | null): CommunityEmbed | null => {
    if (typeof community === 'undefined' || !community) return null
    return {
      id: community?.ID || -1,
      guestTitle: community?.guest_title || '',
      guestDesc: community?.guest_desc || '',
      memberTitle: community?.member_title || '',
      memberDesc: community?.member_desc || '',
    }
  },
)

export const formatSiteWideBanner = wrapFormatFn(
  (
    sitewide_banner?: SiteWideBannerApi | null,
    type?: 'article' | 'category' | 'home',
  ): SiteWideBanner | null => {
    if (typeof sitewide_banner === 'undefined' || !sitewide_banner) return null
    return {
      content: sitewide_banner.content || '',
      icon: sitewide_banner.icon || '',
      type: type || 'other',
      link: sitewide_banner.link || '',
      layoutType: sitewide_banner.sitewide_type || 'icon-text',
      bannerDesktop: sitewide_banner?.banner_desktop || '',
      bannerMobile: sitewide_banner?.banner_mobile || '',
      backgroundColor: sitewide_banner?.background_color || '',
      bannerDisplayFrequency: (sitewide_banner?.banner_display_frequency ||
        'daily') as Frequency,
    }
  },
)

export const formatThirdPartyList = wrapFormatFn(
  (data: Integrates3rdPartyApiType | undefined): Integrates3rdPartyType => {
    return {
      chatwoodToken: data?.chatwood_chat || '',
      zaloConfig: {
        oaid: data?.zalo_chat?.oaid || '',
        message: data?.zalo_chat?.message || '',
      },
    }
  },
)

export const formatCategoryApi = wrapFormatFn(
  (category: PageCategoryApi): PageCategory => {
    if (!category) return {} as PageCategory
    return {
      categoryInfo: formatCategory(category.data.category) || null,
      explainersPosts: (category.data.explainers_posts || []).map(
        formatArticle,
      ),
      subCategories: (category.data.sub_categories || []).map(formatCategory),
      experts: category?.data?.experts?.map(formatExpertSummary) || [],
      community: formatCommunityEmbed(category?.data?.category?.community),
      sitewideBanner: formatSiteWideBanner(
        category?.data?.sitewide_banner,
        'category',
      ),
      pregnancy: category?.data?.category?.pregnancy || false,
    }
  },
)

export const formatPaging = wrapFormatFn(
  (paging: PagingRecentPostApi): PagingRecentPost => {
    return {
      currentPage: paging?.current_page || 0,
      perPage: paging?.per_page || 0,
      foundPosts: paging?.found_posts || 0,
      maxNumPages: paging?.max_num_pages || 0,
    }
  },
)

export const formatExpert = wrapFormatFn(
  (expert: ExpertApi): Expert => {
    return {
      id: expert?.user_id || expert?.ID,
      postName: expert.post_name || '',
      postTitle: expert.post_title || '',
      permalink: expert.permalink || '',
      image: expert.image || '',
      imageThumbnail: expert.image_thumbnail || '',
      postContent: expert.post_content || '',
      organizationText: expert.organization_text || '',
      specialty: expert.specialty || '',
      universityDegree: expert.university_degree || '',
      experience: expert.experience || '',
      medicalLicence: expert.medical_license || '',
      metaTag: expert.meta_tag || '',
      facebookUrl: expert.facebook_url || '',
      zaloUrl: expert.zalo_url || '',
      linkedInUrl: expert.linkedin_url || '',
      twitterUrl: expert.twitter_url || '',
      instagramUrl: expert.instagram_url || '',
      emailAddress: expert.email_address || '',
      youtubeUrl: expert.youtube_url || '',
      lineUrl: expert.line_url || '',
      messenger: expert.messenger || '',
      whatsApp: expert.messenger || '',
      google: expert.google || '',
    }
  },
)

export const formatExpertUser = wrapFormatFn(
  (expert: ExpertUserApi): ExpertUser => {
    return {
      id: expert.user_id,
      displayName: expert.display_name,
      description: expert.description,
      profileUrl: expert.profile_url,
      avatar: expert.avatar,
      avatarThumbnail: expert.avatar_thumbnail,
      role: expert.role,
      organisation: expert.organisation || '',
      organisationUrl: expert.organisation_url || '',
      fields: expert.fields,
      metaTag: expert.meta_tag,
      turnOffProfile: expert?.turn_off_profile,
      specialty: expert?.specialty || '',
    }
  },
)

export const formatExpertSummary = wrapFormatFn(
  (expert: ExpertUserSummaryApi): ExpertUserSummary => {
    return {
      id: expert.user_id || '',
      displayName: innertext(expert.display_name) || '',
      description: expert.description || '',
      profileUrl: formatUserProfileUrl(expert) || '',
      avatar: expert.avatar || '',
      avatarThumbnail: expert.avatar_thumbnail || '',
      avatarLarge: expert.avatar_large || '',
      role: expert.role || '',
      slug: expert?.slug || '',
      specialty: expert?.specialty || '',
      organisation: expert?.organisation || '',
      organisationUrl: expert?.organisation_url || '',
      turnOffProfile: expert?.turn_off_profile,
    }
  },
)

export const formatSpotlightExpert = wrapFormatFn(
  (expert: SpotlightExpertApi): SpotlightExpert => {
    return {
      id: expert.user_id,
      displayName: expert.display_name,
      profileUrl: expert?.turn_off_profile ? '' : expert?.profile_url,
      avatarThumbnail: expert.avatar_thumbnail,
      role: expert.role,
      formerRole: expert.former_role,
      specialty: expert?.specialty || '',
      organisation: expert?.organisation || '',
      organisationUrl: expert?.organisation_url || '',
      turnOffProfile: expert?.turn_off_profile,
    }
  },
)

export const formatSpotlightBanner = wrapFormatFn(
  (
    banner: SpotlightPageBannerApi,
    type?: SpotlightTemplate,
  ): SpotlightPageBanner => {
    if (type === 'super_content') {
      return {
        ...banner,
        items:
          banner?.items?.map(
            (
              item: SpotlightSuperContentBannerApi,
            ): SpotlightSuperContentBanner => ({
              title: item.title,
              description: item.description,
              position: item.position,
              banner: item?.mobile_banner || item.background,
              desktopBanner: item.background,
            }),
          ) || [],
      }
    }
    return {
      ...banner,
      items: [],
    }
  },
)

export const formatSpotlightLandingBanner = wrapFormatFn(
  (banner?: SpotlightLandingPageBannerApi[]): SpotlightLandingPageBanner => {
    const mainBanner = banner?.[0] || null
    return {
      title: mainBanner?.title || '',
      description: mainBanner?.description || '',
      desktopImage: mainBanner?.desktop_image || '',
      mobileImage: mainBanner?.mobile_image || '',
      buttonText: mainBanner?.button_text || '',
      buttonUrl: mainBanner?.button_url || '',
    }
  },
)

export const formatSpotlightVideosURL = wrapFormatFn(
  (videoURL: string): string => {
    return videoURL.includes('facebook.com') || videoURL.includes('fb.com')
      ? `https://www.facebook.com/plugins/video.php?href=${videoURL}`
      : videoURL.replace('watch?v=', 'embed/')
  },
)

export const formatSpotlightVideos = wrapFormatFn(
  (spotlightVideos: SpotlightVideos): SpotlightVideos | null => {
    if (typeof spotlightVideos === 'undefined' || !spotlightVideos) return null
    return {
      ...spotlightVideos,
      videos: spotlightVideos.videos?.map((video) => ({
        video: formatSpotlightVideosURL(video.video),
      })),
    }
  },
)

export const formatSpotlightFaqs = wrapFormatFn(
  (faq: {
    question: string
    answer: string
    readmore: {
      post_title: string
      permalink: string
    }
  }): FAQs => {
    if (!faq) return {} as FAQs

    return {
      title: faq?.question,
      content: faq?.answer,
      learnMore: faq?.readmore
        ? {
            title: faq.readmore.post_title,
            permalink: faq.readmore.permalink,
          }
        : null,
    } as FAQs
  },
)

const formatSpotlightWorkshop = wrapFormatFn(
  (workshop?: {
    title: string
    description: string
    sections: WorkshopApi[]
  }): {
    title: string
    description: string
    sections: Workshop[]
  } | null => {
    if (!workshop) return null
    return {
      title: workshop?.title || '',
      description: workshop?.description || '',
      sections:
        workshop?.sections?.map((section) => ({
          active:
            typeof section?.inactive_section !== 'boolean' ||
            !section?.inactive_section,
          name: section?.name || '',
          location: section?.location || '',
          type: section?.type || '',
          time: `${dayjs(section.date, POST_DATE_FORMAT).format('dddd')}, ${
            section.date
          }`,
          timeRange: `${section?.start_time} - ${section?.end_time}`,
          image: section?.image || '',
          influencers: section?.influencers,
          campaignId: section?.campaign_id || '',
          locationUrl: section?.location_url || '',
        })) || [],
    }
  },
)

export const formatGameBanner = wrapFormatFn(
  (game: GameApi): Game => {
    if (!game) return {} as Game

    return {
      desktopBanner: game.desktop_banner || '',
      mobileBanner: game.mobile_banner || '',
      url: game.url || '',
    }
  },
)

export const formatSpotlightGaming = wrapFormatFn(
  (gaming?: {
    game: GameApi
    mechanism: {
      title: string
      description: string
      steps: MechanismStep[]
    }
    reward: {
      image: string
      items: RewardItems[]
    }
  }): {
    game: Game
    mechanism: {
      title: string
      description: string
      steps: MechanismStep[]
    }
    reward: {
      image: string
      items: RewardItems[]
    }
  } | null => {
    if (!gaming) return null

    return {
      game: formatGameBanner(gaming?.game) || {},
      mechanism: {
        title: gaming?.mechanism?.title || '',
        description: gaming?.mechanism?.description || '',
        steps: gaming?.mechanism.steps || [],
      },
      reward: {
        image: gaming.reward.image || '',
        items: gaming.reward.items || [],
      },
    }
  },
)

export const formatCarePathGroup = wrapFormatFn(
  (data: CarePathGroupApi): CarePathGroup => {
    return {
      termId: data?.group_term_id || '',
      groupId: data?.group_id || '',
      name: data?.name || '',
      icon: data?.icon || '',
      colorPatterns: data?.color_patterns || CARE_PATH_DEFAULT_COLOR_PATTERN,
      additionalSlug: data?.additional_slug || [],
    }
  },
)

export const formatSpotlight = wrapFormatFn(
  (spotlight: SpotlightApi): Spotlight => {
    if (!spotlight) return {} as Spotlight
    const structuredContent =
      spotlight.type === 'default' || spotlight.type === 'landing'

    return {
      id: spotlight.ID,
      postName: spotlight.post_name || '',
      postTitle:
        spotlight.seo_settings?.page_title || spotlight.post_title || '',
      permalink: spotlight.permalink,
      startDate: spotlight.start_date,
      endDate: spotlight.end_date,
      sponsor: spotlight.sponsor,
      banner: formatSpotlightBanner(spotlight.banner, spotlight.type),
      communityTitle: spotlight.community_title || '',
      community: formatCommunityEmbed(spotlight.community),
      posts: structuredContent
        ? spotlight.posts.map((section) => ({
            ...section,
            typeView: section.type_view,
            enableVideo: !!section?.enable_video,
            post: section.post.map(formatArticle),
          }))
        : [],
      healthTool: structuredContent ? spotlight.health_tool : '',
      videos: structuredContent
        ? formatSpotlightVideos(spotlight.videos)
        : null,
      expert: {
        title: spotlight.expert?.title || '',
        experts: spotlight.expert?.experts?.map(formatSpotlightExpert) || [],
      },
      metaTag: spotlight.meta_tag || '',
      isSponsor: spotlight.is_sponsor || false,
      isPremium: spotlight.is_premium || false,
      type: spotlight.type || 'default',
      content:
        spotlight.type === 'super_content' ? spotlight.content || '' : '',
      sources: spotlight.sources || '',
      translateOptions: formatTranslateOptions(spotlight.translate),
      faqs: (spotlight.faqs?.data || []).map(formatSpotlightFaqs),
      faqsTitle: spotlight.faqs?.title || '',
      subot: {
        title: spotlight.subot?.title || '',
        post: spotlight.subot?.post?.map(formatSingleSubot) || [],
      },
      image: spotlight.image || '',
      imageMedium: spotlight.image_medium || '',
      imageThumbnail: spotlight.image_thumbnail || '',
      workshop: formatSpotlightWorkshop(spotlight.workshop),
      gaming: formatSpotlightGaming(spotlight.gaming),
      clickable_banners: spotlight.clickable_banners || null,
      disableAd: Boolean(spotlight.disable_ad),
      titleGroup: spotlight.title_group || '',
      descriptionGroup: spotlight.description_group || '',
      listGroup: (spotlight.list_group || []).map(formatCarePathGroup),
      additionalSlugActive: spotlight.additional_slug_active || '',
      groupIdActive: spotlight.group_id_active || '',
      colorPatterns:
        spotlight.color_patterns || CARE_PATH_DEFAULT_COLOR_PATTERN,
      generalGroup: {
        termId: spotlight.general_group?.group_term_id || '',
        groupId: spotlight.general_group?.group_id || '',
        additionalSlug: spotlight.general_group?.additional_slug || '',
      },
      listSessions: (spotlight.list_sessions || []).map((i, idx) => ({
        sessionId: i.session_layout + idx,
        ...formatSpotlightSession(i),
      })),
      integrates3rdParty: formatThirdPartyList(
        spotlight['integrates-3rd-party'],
      ),
      intensivePopup: {
        leavingPopup: {
          title: spotlight.intensive_popup?.leaving_site?.title?.value || '',
          titleColor:
            spotlight.intensive_popup?.leaving_site?.title?.color || '',
          buttonColor:
            spotlight.intensive_popup?.leaving_site?.color_setting
              ?.button_color || '',
          message:
            spotlight.intensive_popup?.leaving_site?.message?.value || '',
          messageColor:
            spotlight.intensive_popup?.leaving_site?.message?.color || '',
          backgroundColor:
            spotlight.intensive_popup?.leaving_site?.color_setting
              ?.background_color || '',
          desktopImage:
            typeof spotlight.intensive_popup?.leaving_site?.icon_desktop ===
            'string'
              ? spotlight.intensive_popup.leaving_site.icon_desktop
              : '',
          mobileImage:
            typeof spotlight.intensive_popup?.leaving_site?.icon_mobile ===
            'string'
              ? spotlight.intensive_popup.leaving_site.icon_mobile
              : '',
        },
        idlePopup: {
          title: spotlight.intensive_popup?.idle_state?.title?.value || '',
          titleColor: spotlight.intensive_popup?.idle_state?.title?.color || '',
          buttonColor:
            spotlight.intensive_popup?.idle_state?.color_setting
              ?.button_color || '',
          backgroundColor:
            spotlight.intensive_popup?.idle_state?.color_setting
              ?.background_color || '',
          desktopImage:
            typeof spotlight.intensive_popup?.idle_state?.desktop === 'string'
              ? spotlight.intensive_popup?.idle_state?.desktop
              : '',
          mobileImage:
            typeof spotlight.intensive_popup?.idle_state?.mobile === 'string'
              ? spotlight.intensive_popup.idle_state?.mobile
              : '',
        },
      },
      logo: {
        icon: spotlight.logo?.icon || '',
        url: spotlight?.logo?.url || '',
      },
      keyID: spotlight.key_identifier || '',
      hasSocialShare: Boolean(spotlight.sharing_social_channel),
      adsOnArticles: {
        ads_on_articles_for_desktop: !!spotlight?.ads_on_articles_for_desktop,
        ads_on_articles_for_mobile: !!spotlight?.ads_on_articles_for_mobile,
      },
    }
  },
)

export const formatSingleSubot = wrapFormatFn(
  (singleSubot: SingleSubotApi): SingleSubot => {
    if (!singleSubot) return {} as SingleSubot
    return {
      title: singleSubot?.title || '',
      englishName: singleSubot?.english_name || '',
      icon: singleSubot?.icon || '',
      subotUrl: singleSubot?.subot_url || '',
    }
  },
)

export const formatSpotlightsList = wrapFormatFn(
  (spotlights: SpotlightSummaryApi[]): SpotlightSummary[] => {
    if (!spotlights) return []
    return spotlights.map(formatSpotlightsSummary) as SpotlightSummary[]
  },
)

export const formatSpotlightsSummary = wrapFormatFn(
  (spotlight?: SpotlightSummaryApi): SpotlightSummary | null => {
    if (typeof spotlight === 'undefined' || !spotlight) return null
    return {
      buttonDisplay: spotlight.button_display || null,
      id: spotlight.ID,
      name: spotlight.name,
      permalink: spotlight.permalink,
      image: spotlight?.image || '',
      smallBanner: spotlight?.small_banner || '',
      imageMedium: spotlight?.image_medium || null,
      imageThumbnail: spotlight?.image_thumbnail || null,
      desktopBanner: spotlight?.desktop_banner || '',
      mobileBanner: spotlight?.mobile_banner || '',
      startDate: spotlight?.start_date,
      endDate: spotlight?.end_date,
      isPremium: spotlight?.is_premium || false,
    }
  },
)

export const formatRecentPost = wrapFormatFn(
  (recentPost: RecentPostApi): RecentPost => {
    if (!recentPost) return {} as RecentPost

    return {
      articles: (recentPost.data.articles || []).map(formatArticle),
      posts: (recentPost.data.posts || []).map(formatArticle),
      paging: formatPaging(recentPost.data.paging),
    }
  },
)

export const formatStaticPageData = wrapFormatFn(
  (data: StaticPageApi): StaticPage => {
    if (!data) return {} as StaticPage

    return {
      id: data.ID,
      postTitle: data.post_title,
      modifiedTime: data.modified_time,
      featureImage: data.feature_image,
      postContent: data.post_content,
      outlinesLink: data?.outlines_link || [],
      meta_tag: data.meta_tag || '',
    }
  },
)

export const formatHealthToolTypeCategory = wrapFormatFn(
  (category: CategoryHealthToolApi): HealthToolByCategory => {
    if (!category) return {} as HealthToolByCategory

    return {
      termId: category?.term_id || 0,
      name: category?.name || '',
      healthTools: category?.health_tools?.map(formatHealthTool) || [],
    }
  },
)

export const formatHealthTool = wrapFormatFn(
  (healthTool: HealthToolApi): HealthTool => {
    if (!healthTool) return {} as HealthTool

    return {
      id: healthTool.id,
      isSubot: healthTool.is_subot,
      template: healthTool.template,
      icon: healthTool.icon,
      link: healthTool.link?.startsWith('http')
        ? healthTool.link
        : `${basePath}/health-tools` +
          healthTool.link?.replace(`${basePath}/`, '/'),
      subtitle: healthTool.subtitle,
      title: healthTool.title,
      isMobileAppTool: Boolean(healthTool.is_mobile_app_tool),
    }
  },
)

export const formatArticleMetaData = wrapFormatFn(
  (data: MetaTagApi): MetaTag => {
    if (!data) return {} as MetaTag

    return {
      title: data.title || '',
      description: data.description || '',
      locale: data.locale || null,
      url: data.url || null,
      siteName: data.site_name || '',
      publishedTime: data.published_time || '',
      modifiedTime: data.modified_time || '',
      updatedTime: data.updated_time || '',
      image: data.image || '',
      width: Number(data.width || 150),
      height: Number(data.height || 150),
      tags:
        data.tags?.map((tag) => ({
          name: tag.name || '',
          slug: tag.slug || '',
        })) || [],
      openGraph: data.open_graph
        ? {
            title: data.open_graph.title,
            description: data.open_graph.description,
            image: data.open_graph.image,
            imageWidth: data.open_graph.image_width,
            imageHeight: data.open_graph.image_height,
          }
        : {},
      twitter: data.twitter
        ? {
            title: data.twitter.title,
            description: data.twitter.description,
            image: data.twitter.image,
          }
        : {},
    } as MetaTag
  },
)

export const formatCategoryMetaTag = wrapFormatFn(
  (data: CategoryMetaApi): CategoryMeta => {
    if (!data) return {} as CategoryMeta

    return {
      title: data.title || '',
      description: data.description || '',
      locale: data.locale || null,
      url: data.url || null,
      siteName: data.site_name || '',
      keyword: data.keyword || '',
    } as CategoryMeta
  },
)

export const formatMedicalReviewedData = wrapFormatFn(
  (data: MedicalReviewApi): MedicalReview => {
    if (!data?.title) return {} as MedicalReview

    return {
      title: data?.title || '',
      description: data?.description || '',
      items:
        data.items?.map((item) => ({
          title: item.title,
          description: item.description,
          imageUrl: item.image_url,
        })) ?? [],
    }
  },
)

export const formatCollectingForm = wrapFormatFn(
  (data: CollectingDataFormApi): CollectingDataForm => {
    return {
      collectingTypes: data?.collecting_types || CollectingDataFormType.NONE,
      collectingData: {
        campaigns: data?.collecting_data?.campaigns || null,
        banner: {
          desktop: data?.collecting_data?.banner?.desktop || '',
          mobile: data?.collecting_data?.banner?.mobile || '',
        },
      },
      phoneVerification: data?.phone_verification || PhoneVerifyType.SMS,
      isSkippable: data?.is_skippable || false,
    }
  },
)

export const formatAdditionalQuestion = wrapFormatFn(
  (data: AdditionalQuestionFieldTypeApi): AdditionalQuestionFieldType => {
    if (!data) return {} as AdditionalQuestionFieldType
    return {
      question: {
        id: data.question.id,
        question: data.question.question || '',
        questionHint: data.question.tooltip_question || '',
      },
      answers: data.answers || [],
      hasAnother: Boolean(data.has_another),
      isMulti: Boolean(data.select_multi),
      otherInputPlaceholder: data.placeholder || '',
    }
  },
)

export const formatCommonHealthtoolForm = wrapFormatFn(
  (data: CommonHealthtoolInfoApi): CommonHealthtoolInfo => {
    return {
      title: data.title || '',
      description: data.description || '',
      ...formatCollectingForm(data),
      listQuestions: (data.list_questions || []).map(formatAdditionalQuestion),
      additionalInfo: formatAdditionalInfo(data.sponsor_banner),
    }
  },
)
export const formatAdditionalInfo = wrapFormatFn(
  (data: AdditionalInfoApi): AdditionalInfo => {
    return {
      title: data?.further_result?.title || '',
      description: data?.further_result?.Content || '',
      image: data?.image || '',
      ctaUrl: data?.further_result?.link?.url || '',
      ctaTitle: data?.further_result?.link?.title || '',
    }
  },
)
export const formatBMIContent = wrapFormatFn(
  (data: ToolContentApi): ToolContent => {
    if (!data || !data.information) return {} as ToolContent

    return {
      enable_ad: !!data?.enable_ad,
      is_sponsored: !!data?.is_sponsored,
      sponsor: data?.sponsor || null,
      featured_image: data?.featured_image,
      information:
        data.information?.map?.((info) => ({
          title: info.title,
          description: info.description,
        })) ?? [],
      reviewBy: {
        avatar: data.reviewed_by?.avatar_thumbnail || '',
        reviewer:
          data.reviewed_by?.display_name || data.reviewed_by?.name || '',
        date: data.reviewed_date || '',
        link: formatUserProfileUrl(data?.reviewed_by),
      },
      metaTag: data?.meta_tag || '',
      disclaimer: data?.disclaimer || '',
      isLeadgen: Boolean(data.is_leadgen),
      ...formatCommonHealthtoolForm(data),
    }
  },
)
export const formatTHRContent = wrapFormatFn(
  (data: ThrInfoApi | null): ThrInfo => {
    if (!data) return {} as ThrInfo
    return {
      enable_ad: !!data?.enable_ad,
      disclaimer: data?.disclaimer || '',
      featured_image: data?.featured_image || '',
      info: data.info || [],
      reviewedBy: data.reviewed_by ? formatUserProfile(data.reviewed_by) : null,
      reviewedDate: data?.reviewed_date || '',
      metaTag: data?.meta_tag || '',
      reviewBy: {
        avatar: data.reviewed_by?.avatar_thumbnail || '',
        reviewer:
          data.reviewed_by?.display_name || data.reviewed_by?.name || '',
        date: data.reviewed_date || '',
        link: formatUserProfileUrl(data?.reviewed_by),
      },
      isLeadgen: Boolean(data.is_leadgen),
      ...formatCommonHealthtoolForm(data),
    } as ThrInfo
  },
)

export const formatPWGResult = wrapFormatFn(
  (data: PWGResultApi): PWGResult => {
    if (!data) return {} as PWGResult

    return {
      enable_ad: !!data?.enable_ad,
      assessmentData: [
        {
          tab: 'pregnancyWeightGain.explaination',
          defaultMessage: 'Explaination',
          dataEventCategory: 'Health Tool',
          dataEventAction: 'Result Explanation Click',
          dataEventLabel: window.location.href,
          content: data?.explaination ?? '',
        },
        {
          tab: 'pregnancyWeightGain.suggestion',
          defaultMessage: 'Suggestion',
          dataEventCategory: 'Health Tool',
          dataEventAction: 'Recommendation Click',
          dataEventLabel: window.location.href,
          content: data?.suggestion ?? '',
        },
      ],
      faqs: data?.faqs ?? [],
      suggestedArticlesLink: data?.suggested_articles_link ?? '',
      suggestedArticles: data?.suggested_articles?.map(formatArticle) ?? [],
      disclaimer: data?.disclaimer ?? '',
      weightByWeek:
        data.weight_by_week?.map((w) => ({
          week: w.week,
          min: w.min,
          max: w.max,
        })) ?? [],
      care: data.care,
      pregnancyNavigation: data?.embeb.pregnacy_navigation || [],
      status: data?.status,
    }
  },
)

export const formatTHRResult = wrapFormatFn(
  (data: THRResultApi): THRResult => {
    if (!data) return {} as THRResult

    return {
      assessmentData: [
        {
          tab: 'pregnancyWeightGain.explaination',
          defaultMessage: 'Explaination',
          content: data?.explaination ?? '',
        },
        {
          tab: 'pregnancyWeightGain.suggestion',
          defaultMessage: 'Suggestion',
          content: data?.suggestion ?? '',
        },
      ].filter((i) => i.content),
      faqs: data?.faqs ?? [],
      suggestedArticlesLink: data?.suggested_articles_link ?? '',
      suggestedArticles: data?.suggested_articles?.map(formatArticle) ?? [],
      disclaimer: data?.disclaimer ?? '',
      idealHeartRate: {
        percent: data?.ideal_heart_rate?.percent ?? 0,
        range: data?.ideal_heart_rate?.range ?? '',
      },
      maximumHeartRate: data?.maximum_heart_rate ?? 0,
      restingHeartRate: {
        heartRate: data?.resting_heart_rate?.point ?? 0,
        status: (data?.resting_heart_rate?.value as PwgResult) || 'Athlete',
        chart:
          data?.resting_heart_rate?.chart?.map((w) => ({
            level: w.level,
            markup: w.markup,
          })) ?? [],
      },
      care: data.care ?? null,
      community: data.community || null,
    }
  },
)

export const formatPWGContent = wrapFormatFn(
  (data: PwgInfoApi | null): PwgInfo => {
    if (!data) return {} as PwgInfo
    return {
      enable_ad: !!data?.enable_ad,
      disclaimer: data?.disclaimer || '',
      featuredImage: data?.featured_image || '',
      info: data?.info || [],
      reviewedBy: data.reviewed_by ? formatUserProfile(data.reviewed_by) : null,
      reviewBy: {
        avatar: data.reviewed_by?.avatar_thumbnail || '',
        reviewer:
          data.reviewed_by?.display_name || data.reviewed_by?.name || '',
        date: data.reviewed_date || '',
        link: formatUserProfileUrl(data?.reviewed_by),
      },
      reviewedDate: data?.reviewed_date || '',
      metaTag: data?.meta_tag || '',
      pregnancyNavigation: data?.pregnacy_navigation || null,
      isLeadgen: Boolean(data.is_leadgen),
      ...formatCommonHealthtoolForm(data),
    } as PwgInfo
  },
)

export const formatBMIResult = wrapFormatFn(
  (data: BMIResultApi, statusText = ''): BMIResult => {
    return {
      enable_ad: !!data?.enable_ad,
      status: (data?.status as BMICondition) || 'healthy',
      statusText,
      comparison: data?.comparison ?? '',
      assessmentData: [
        {
          tab: 'tools.condition',
          content: data?.condition ?? '',
        },
        {
          tab: 'tools.risks',
          content: data?.risk ?? '',
        },
        {
          tab: 'tools.suggestion',
          content: data?.suggestion ?? '',
        },
      ],
      chartData:
        data?.chart_data && data.chart_data?.length ? data.chart_data : [],
      disclaimer: data?.disclaimer ?? '',
      illustrator: data?.illustrator ?? '',
      faqs: data?.faqs ?? [],
      suggestedArticlesLink: data?.suggested_articles_link ?? '',
      suggestedArticles: data?.suggested_articles?.map(formatArticle) ?? [],
      care: data?.care || [],
      community: data?.community || null,
      bmiRange: data?.bmi_range || null,
      pnSuggestCategory: data?.pn_suggest_category || [],
      tdeeValue: data?.value_tdee || 0,
    }
  },
)

export const formatReviewedCategory = wrapFormatFn(
  (data: ReviewedCategoryApi): ReviewedCategory => {
    if (!data) return null
    return {
      ID: data.ID || 0,
      userId: data.user_id || 0,
      formerRole: data.former_role || '',
      role: data.role || '',
      slug: data.slug || '',
      displayName: data.display_name || '',
      description: data.description || '',
      profileUrl: data.profile_url || '',
      avatar: data.avatar || '',
      avatarThumbnail: data.avatar_thumbnail || '',
      turnOffProfile: data.turn_off_profile || false,
      specialty: data.specialty || '',
      organisation: data.organisation || '',
      organisationUrl: data.organisation_url || '',
    }
  },
)

export const formatFaqsCategory = wrapFormatFn(
  (data: FAQsApi): FAQs => {
    return {
      title: data.title,
      content: data.content,
      learnMore: data.readmore ? data.readmore : null,
    }
  },
)

export const formatOvulationSuggestedArticle = wrapFormatFn(
  (data: OvulationSuggestedArticleApi): OvulationSuggestedArticle => {
    if (!data) return {} as OvulationSuggestedArticle
    console.log('** RYAN index.ts 1429 data : ', data)

    return {
      suggestedArticles: data?.suggested_articles?.map(formatArticle) ?? [],
      suggestedArticlesLink: data?.suggested_articles_link ?? '',
    }
  },
)
export const formatOvulationInfo = wrapFormatFn(
  (data: OvulationInfoApi | null): OvulationInfo => {
    if (!data) return {} as OvulationInfo

    return {
      enable_ad: !!data?.enable_ad,
      is_sponsored: data?.is_sponsored,
      featuredImage: data?.featured_image || '',
      sponsor: data.sponsor,
      disclaimer: data.disclaimer,
      info: data.info || [],
      reviewedBy: data.reviewed_by
        ? {
            id: data.reviewed_by.user_id || data.reviewed_by.ID,
            permalink: formatUserProfileUrl(data?.reviewed_by),
            name: data.reviewed_by.display_name || data.reviewed_by.name,
            postTitle: data.reviewed_by.post_title || '',
          }
        : null,
      reviewBy: {
        avatar: data.reviewed_by?.avatar_thumbnail || '',
        reviewer:
          data.reviewed_by?.display_name || data.reviewed_by?.name || '',
        date: data.reviewed_date || '',
        link: formatUserProfileUrl(data?.reviewed_by),
      },
      reviewedDate: data.reviewed_date,
      metaTag: data.meta_tag,
      community: data.community || null,
      isLeadgen: Boolean(data.is_leadgen),
      ...formatCommonHealthtoolForm(data),
    } as OvulationInfo
  },
)
const NO_DECIMAL = [LOCALE.Vietnam, LOCALE.Indonesia]

export const formatLocalePrice = wrapFormatFn(
  (price: number | string, locale: string, currency: string): string => {
    const parsePrice = (typeof price !== 'number' && parseFloat(price)) || price
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'VND',
      ...(NO_DECIMAL.includes(locale as LOCALE)
        ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        : {}),
    })
    return `${formatter.format(Number(parsePrice))}`
  },
)

export const formatAffiliateChannel = wrapFormatFn(
  (channel: AffiliateChannel): AffiliateChannel => {
    return {
      ...channel,
      price: channel?.price,
    }
  },
)

export const formatAffiliatePriceRange = wrapFormatFn(
  (
    locale: string,
    currency: AffiliateCurrency,
    channels?: AffiliateChannel[],
  ): string => {
    if (
      typeof channels === 'undefined' ||
      (Array.isArray(channels) && channels.length === 0)
    )
      return ''
    const priceList = channels?.map(
      (channel) => parseFloat(channel?.price) || channel?.price,
    )

    if (!priceList) {
      return ''
    }
    const lowestPrice = priceList.reduce((a, b) => (a < b ? a : b))
    const highestPrice = priceList.reduce((a, b) => (a > b ? a : b))
    if (!lowestPrice || !highestPrice || lowestPrice === highestPrice) {
      return lowestPrice || highestPrice
        ? formatLocalePrice(lowestPrice || highestPrice, locale, currency?.code)
        : ''
    }
    return `${formatLocalePrice(
      lowestPrice,
      locale,
      currency?.code,
    )} ~ ${formatLocalePrice(highestPrice, locale, currency?.code)}`
  },
)

export const formatAffiliateProductCategories = wrapFormatFn(
  (
    categories?: AffiliateProductCategoryApi[] | null,
  ): AffiliateProductCategory[] => {
    if (typeof categories === 'undefined' || !categories) return []
    return categories?.map((category) => ({
      name: category?.name,
      permalink: category?.permalink,
      termId: category?.term_id,
      textColor: category?.text_color,
    }))
  },
)

export const formatAffiliateProductBrand = wrapFormatFn(
  (
    brands?: AffiliateProductCategoryApi[] | null,
  ): AffiliateProductCategory[] => {
    if (typeof brands === 'undefined' || !brands) return []
    return brands?.map((brand) => ({
      name: brand?.name,
      permalink: brand?.permalink?.includes('https') ? brand?.permalink : '',
      termId: brand?.term_id,
      textColor: brand?.text_color,
    }))
  },
)

export const formatAffiliateProduct = wrapFormatFn(
  (
    product?: AffiliateProductApi | null,
    locale?: string,
  ): AffiliateProduct | null => {
    if (typeof product === 'undefined' || !product) return null

    return {
      id: product.ID,
      sku: product?.sku || '',
      postName: product?.post_name || '',
      postTitle: product?.post_title || '',
      permalink: product?.permalink || '',
      brand: formatAffiliateProductBrand(product?.brand),
      productCat: formatAffiliateProductCategories(product?.product_cat),
      image: product?.image || '',
      imageMedium: product?.image_medium || '',
      imageThumbnail: product?.image_thumbnail || '',
      gallery: product?.gallery,
      channel: product?.channel?.map((channel) =>
        formatAffiliateChannel(
          channel,
          // product?.currency_unit,
          // locale || 'en-PH',
        ),
      ),
      currencyUnit: product?.currency_unit,
      priceRange: formatAffiliatePriceRange(
        locale || 'en-PH',
        product?.currency_unit,
        product?.channel,
      ),
      pros: product?.pros,
      cons: product?.cons,
      ctaButton: product?.cta_button,
    }
  },
)

export const formatCareDoctorInfo = wrapFormatFn(
  (doctor?: CareDoctorInfoApi): CareDoctor => {
    if (!doctor) return {} as CareDoctor

    return {
      name: doctor.name || '',
      avatar: doctor.avatar || '/images/avatar.jpg',
      price: doctor.price || '',
      phone: doctor.phone || '',
      priceUnit: doctor.price_unit_discover || '',
      originalPrice: doctor.original_price || '',
      provideCare: doctor.provide_care || 0,
      id: doctor.id,
      hospital: doctor.hospital_branch || ({} as CareHospital),
      specialties:
        doctor.specialties.length > 0
          ? doctor.specialties.map((e: CareSpecialties) => e.name).join(', ')
          : '',
      doctorSpecialties: doctor.specialties,
      specialtiesTransform: (doctor.specialties_transform || []).map((i) => ({
        id: i.id || '',
        slug: i.slug || '',
      })),
      slug: doctor.slug,
      validOutpatient: doctor.valid_outpatient || false,
      validTelemedicine: doctor.valid_telemedicine || false,
      rating: doctor.rating || '',
      position: doctor.position || 0,
    }
  },
)

export const formatCareServiceInfo = wrapFormatFn(
  (service?: CareServiceInfoApi): CareService => {
    if (!service) return {} as CareService

    return {
      name: service.name || '',
      logo: service.logo || '/images/avatar.jpg',
      image: service.image || '',
      price: service.price || '',
      phone: service.phone || '',
      slug: service.slug || '',
      priceUnit: service.price_unit_discover || '',
      originalPrice: service.original_price || '',
      provideCare: service.provide_care || 0,
      id: service.id,
      hospital: service.hospital_branch || ({} as CareHospital),
      serviceId: service.service_id,
      position: service.position || 0,
    }
  },
)
const ArticleReactionCount = (count?: number): string => {
  if (typeof count !== 'number' || count < 1000) return ''
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ]
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  const item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return count >= item.value
    })
  return item
    ? (count / item.value).toFixed(1).replace(rx, '$1') + item.symbol
    : '0'
}

export const formatArticleReaction = wrapFormatFn(
  (reaction: ArticleReactionApi): ArticleReaction => {
    return {
      like: ArticleReactionCount(reaction?.total_like),
      dislike: ArticleReactionCount(reaction?.total_dislike),
    }
  },
)

export const formatDefaultCarousel = wrapFormatFn(
  (carousel: DefaultCarouselApi): DefaultCarousel => {
    return {
      title: carousel?.title || '',
      description: carousel?.subtitle || '',
      logoImage: carousel?.icon || '',
      mid: carousel?.items
        ? carousel?.items?.map((e) => ({
            img: e?.icon || '',
            label: e?.title || '',
          }))
        : [],
      legends: carousel?.contents
        ? carousel?.contents?.map((e) => ({
            icon: e?.icon || '',
            label: e?.content || '',
          }))
        : [],
    }
  },
)

export const formatStatisticCarousel = wrapFormatFn(
  (carousel: StatisticCarouselApi): StatisticCarousel => {
    return {
      title: carousel?.title || '',
      imageDesktop: carousel?.image_desktop || '',
      imageMobile: carousel?.image_mobile || '',
    }
  },
)

export const formatBabyVaccinationRecords = wrapFormatFn(
  (record: BabyVaccinationRecordsApi): BabyVaccinationRecord => {
    if (!record) return {} as BabyVaccinationRecord
    let status: BabyVaccinationStatus = 'vaccinated'

    const isVaccinated = record.status
    if (!isVaccinated) {
      const givenOnParsed = dayjs(record.given_on, API_DATE_FORMAT)
      status = givenOnParsed.isBefore(dayjs()) ? 'passed' : 'next'
    }
    return {
      id: record?.id || '',
      vaccineId: record?.vaccine_id || '',
      vaccineName: record?.vaccine_name || '',
      vaccineAntigen: record?.vaccine_antigen || '',
      givenOn: record?.given_on || '',
      doseNumber: record?.dose_number || 0,
      totalDoses: record?.total_doses || 0,
      status: status,
      optional: record?.vaccine_optional || false,
    } as BabyVaccinationRecord
  },
)

export const formatBabyVaccinationInfoResultPage = wrapFormatFn(
  (data: InfoBabyVaccineResultPageApi): InfoBabyVaccineResultPage => {
    if (!data) return {} as InfoBabyVaccineResultPage
    return {
      faqs: data?.faqs ?? [],
      suggestedArticlesLink: data?.suggested_articles_link ?? '',
      suggestedArticles: data?.suggested_articles?.map(formatArticle) ?? [],
    }
  },
)
export const formatBabyVaccinationMainTimelines = wrapFormatFn(
  (
    mainTimelines: BabyVaccinationMainTimelinesApi,
  ): BabyVaccinationMainTimelines => {
    if (!mainTimelines) return {} as BabyVaccinationMainTimelines
    return {
      id: mainTimelines?.id,
      name: mainTimelines?.name,
      vaccines: mainTimelines?.vaccines?.map((vaccine) => ({
        id: vaccine?.id,
        name: vaccine?.name,
        antigen: vaccine?.antigen,
        totalDoses: vaccine?.total_doses,
        optional: vaccine?.optional || false,
        records: vaccine?.records?.map(formatBabyVaccinationRecords),
      })),
      timelines: mainTimelines?.timelines?.map((timeline) => ({
        id: timeline?.id,
        name: timeline?.name,
        shortName: timeline?.short_name,
      })),
    } as BabyVaccinationMainTimelines
  },
)

export const formatBabyVaccinationVaccineInfo = wrapFormatFn(
  (vaccine: BabyVaccinationVaccineInfoApi): BabyVaccinationVaccineInfo => {
    if (!vaccine) return {} as BabyVaccinationVaccineInfo
    return {
      id: vaccine.id,
      name: vaccine.name || '',
      antigen: vaccine.antigen || '',
      brandImg: vaccine.brand_img || '',
      benefits: vaccine.benefits || '',
      link: vaccine.link || '',
      contraindications: vaccine.contraindications || '',
      optional: vaccine.optional || false,
      detail: vaccine.detail || '',
      records: vaccine.records?.map(formatBabyVaccinationRecords),
      pregnancyNavigation: vaccine.pregnacy_navigation || [],
    } as BabyVaccinationVaccineInfo
  },
)

export const formatTogetherCommunity = wrapFormatFn(
  (community: TogetherCommunityApi): TogetherCommunity => {
    if (!community) return {} as TogetherCommunity
    return {
      name: community?.name,
      id: community?.id,
      membersCount: community?.members_count,
      postsCount: community?.posts_count,
      topicsCount: community?.topics_count,
      description: community?.description,
      joined: !!community?.joined,
      thumbnail: community?.thumbnail,
      coverImage: community?.banner?.[0]?.image || '',
      mobileCoverImage: community?.banner?.[0]?.mobile_image || '',
      isActive: community?.is_active,
      slug: community?.slug,
    } as TogetherCommunity
  },
)

export const formatTogetherTopic = wrapFormatFn(
  (topic: TogetherTopicApi): TogetherTopic => {
    if (!topic) return {} as TogetherTopic
    return {
      categories: topic?.categories,
      community: {
        description: topic?.community.description,
        name: topic?.community.name,
        id: topic?.community.id,
        slug: topic?.community.slug,
      },
      countMembers: topic?.count_members,
      countPosts: topic?.count_posts,
      description: topic?.description,
      id: topic?.id,
      joined: !!topic?.joined,
      thumbnail: topic?.thumbnail,
      isActive: topic?.is_active,
      name: topic?.name,
      order: topic?.order,
      slug: topic?.slug,
    } as TogetherTopic
  },
)

export const formatTogetherPost = wrapFormatFn(
  (post: TogetherPostApi): TogetherPost => {
    if (!post) return {} as TogetherPost
    return {
      bookmarked: post?.bookmarked,
      commented: Boolean(post.commented) || false,
      commentsCount: post?.comments_count,
      community: {
        description: post?.community.description,
        name: post?.community.name,
        id: post?.community.id,
        slug: post?.community.slug,
      },
      likesCount: post?.likes_count,
      createdAt: post?.created_at,
      createdTime: post?.created_time,
      description: post?.description,
      id: post?.id,
      images: post?.images,
      isActive: post?.is_active,
      isAnonymous: post?.is_anonymous,
      isPin: !!post?.is_pin,
      pinDate: post?.pin_date,
      postType: post?.post_type,
      title: post?.name,
      liked: post?.liked,
      previewUrl: {
        title: post.preview_title || '',
        url: post.preview_link || '',
        image: post.preview_image || '',
        description: post.preview_description || '',
      },
      scheduleDatetime: post?.schedule_datetime,
      slug: post?.slug,
      topics: post?.topics,
      author: {
        avatar: post?.user?.avatar,
        email: post?.user?.email,
        id: post?.user?.id,
        name: post?.user?.name,
        partnerId: post?.user?.partner_id,
        role: post?.user?.role,
        type: post?.user?.type,
        username: post?.user?.user_name,
      },
      mentions: post?.mentions || [],
    } as TogetherPost
  },
)

export const formatTogetherComment = wrapFormatFn(
  (data: TogetherCommentApi): TogetherComment => {
    if (!data) return {} as TogetherComment

    return {
      content: data.content,
      countLikes: data.likes_count,
      countReplies: data.replies_count,
      createdAt: data.created_at,
      createdTime: data.created_time,
      id: String(data.id),
      images: data.image || [],
      link: data.link || null,
      replies:
        data.replies && data.replies.length > 0
          ? data.replies.map(formatTogetherComment)
          : [],
      author: {
        id: data.user?.id || 0,
        username: data.user?.user_name || '',
        name: data.user?.name || '',
        role: data.user?.role
          ? (data.user?.role?.toLowerCase() as Role)
          : ('' as Role),
        avatar: data.user?.avatar || '',
        type: data.user?.type || '',
        email: data.user?.email || '',
        partnerId: data.user?.partner_id || 0,
      },
      liked: !!data.liked,
      mentionedUser: data.mentioned
        ? {
            id: data.mentioned?.id || 0,
            username: data.mentioned?.user_name || '',
            name: data.mentioned?.name || '',
            role: data.mentioned?.role
              ? (data.mentioned?.role?.toLowerCase() as Role)
              : ('' as Role),
            avatar: data.mentioned?.avatar || '',
            type: data.mentioned?.type || '',
            email: data.mentioned?.email || '',
            partnerId: data.mentioned?.partner_id || 0,
          }
        : null,
      mentions: data.mentions || [],
    } as TogetherComment
  },
)

export const formatPregnancyWeek = wrapFormatFn(
  (
    pregnancyProgress: WeekOfPregnancyApi,
    index: number,
  ): WeekOfPregnancy | null => {
    if (!pregnancyProgress?.title) return null
    return {
      id: index,
      name: pregnancyProgress.title,
      fruitIcon: pregnancyProgress.fruit_icon,
      fruitName: pregnancyProgress.fruit_name || '',
      icon: pregnancyProgress.icon,
      height: pregnancyProgress.height,
      weight: pregnancyProgress.weight,
      description: pregnancyProgress.content || '',
      cleanDescription: pregnancyProgress.clean_content || '',
      permalink: pregnancyProgress?.recommended_article?.permalink || '',
    }
  },
)

export const formatBabyVaccinationBanner = wrapFormatFn(
  (data: BabyVaccinationBannerApi): BabyVaccinationBanner => {
    if (!data) return {} as BabyVaccinationBanner
    return {
      image: data?.image || '',
      readMoreUrl: data?.read_more_url || '',
    }
  },
)

export const formatHomepageBanner = wrapFormatFn(
  (data?: HomepageBannerTypeApi): HomepageBannerType | null => {
    const newData: HomepageBannerType = {
      type: data?.type || '',
      banners: {
        title: data?.banners?.title || '',
        description: data?.banners?.description || '',
        image: data?.banners?.image || '',
        ctaText: data?.banners?.cta_text || '',
        ctaUrl: data?.banners?.cta_url || '',
        autoSlideTiming: data?.banners?.auto_slide_timing || 0,
        listBanners: (data?.banners?.list_banners || []).map((i) => ({
          desktop: i.desktop || '',
          mobile: i.mobile || '',
          url: i.url || '',
          openNewTab: Boolean(data?.banners?.open_new_tab),
        })),
      },
      adsOnArticles: {
        ads_on_articles_for_desktop: !!data?.ads_on_articles_for_desktop,
        ads_on_articles_for_mobile: !!data?.ads_on_articles_for_mobile,
      },
    }

    try {
      if (data?.type === 'banner') {
        newData.side_banner = {
          desktop: data?.side_banner?.desktop || '',
          mobile: data?.side_banner?.mobile || '',
          url: data?.side_banner?.url || '',
          openNewTab: Boolean(data?.banners?.open_new_tab),
        }
      }
      if (data?.banner) {
        newData.footer_banner = {
          desktop: data.banner.desktop,
          mobile: data.banner.mobile,
          url: data.banner.url,
          openNewTab: !!data?.open_new_tab,
        }
      }
    } catch (error) {}
    return newData
  },
)

export const formatDoctorSchedule = wrapFormatFn(
  (schedule: ScheduleApi): DoctorSchedule => {
    return {
      appointmentType: schedule.appointment_type,
      started: schedule.started,
      ended: schedule.ended,
      scheduleId: schedule.schedule_id,
    }
  },
)

export const formatDoctorScheduleData = wrapFormatFn(
  (data: DoctorScheduleDataApi): DoctorScheduleData => {
    return {
      doctorId: data.doctor_id,
      workingDay: data.working_day,
      workingTimes: {
        morning: data.working_times.morning
          ? data.working_times.morning.map(formatDoctorSchedule)
          : null,
        afternoon: data.working_times.afternoon
          ? data.working_times.afternoon.map(formatDoctorSchedule)
          : null,
        evening: data.working_times.evening
          ? data.working_times.evening.map(formatDoctorSchedule)
          : null,
      },
      slots:
        (data.working_times.morning?.length || 0) +
        (data.working_times.afternoon?.length || 0) +
        (data.working_times.evening?.length || 0),
    }
  },
)
export const formatServiceScheduleData = wrapFormatFn(
  (data: ServiceScheduleDataApi): ServiceScheduleData => {
    return {
      hospitalServiceId: data.hospital_service_id,
      workingDay: data.working_day,
      workingTimes: {
        morning: data.working_times.morning
          ? data.working_times.morning.map(formatDoctorSchedule)
          : null,
        afternoon: data.working_times.afternoon
          ? data.working_times.afternoon.map(formatDoctorSchedule)
          : null,
        evening: data.working_times.evening
          ? data.working_times.evening.map(formatDoctorSchedule)
          : null,
      },
      slots:
        (data.working_times.morning?.length || 0) +
        (data.working_times.afternoon?.length || 0) +
        (data.working_times.evening?.length || 0),
    }
  },
)

export const formatBabyGrowthContent = wrapFormatFn(
  (data: BabyGrowthContentApi): BabyGrowthContent => {
    if (!data || !data.information) return {} as BabyGrowthContent

    return {
      enable_ad: !!data?.enable_ad,
      information:
        data.information?.map?.((info) => ({
          title: info.title,
          description: info.description,
        })) ?? [],
      reviewBy: {
        avatar: data.reviewed_by?.avatar_thumbnail || '',
        reviewer:
          data.reviewed_by?.display_name || data.reviewed_by?.name || '',
        date: data.reviewed_date || '',
        link: formatUserProfileUrl(data?.reviewed_by),
      },
      metaTag: data.meta_tag || '',
      disclaimer: data.disclaimer || '',
      is_sponsored: data.is_sponsored || false,
      sponsor: data.sponsor,
      featuredImage: data.featured_image || '',
      isLeadgen: Boolean(data.is_leadgen),
      ...formatCommonHealthtoolForm(data),
    }
  },
)

export const formatCareHospitalInfo = wrapFormatFn(
  (hospital?: CareHospitalInfoApi): CareHospital => {
    if (!hospital) return {} as CareHospital

    return {
      address: hospital.address || '',
      city: hospital.city,
      cityId: String(hospital.city_id || ''),
      id: hospital.id,
      latitude: hospital.latitude || '',
      logo: hospital.logo || '/images/avatar.jpg',
      longitude: hospital.longitude || '',
      name: hospital.name || '',
      rating: hospital.rating,
      slug: hospital.slug,
      totalAvailableDoctors: hospital.total_available_doctor,
      totalAvailableServices: hospital.total_available_service,
      totalDoctors: hospital.total_doctor,
      totalReviews: hospital.total_reviews,
      organization: {
        name: hospital.organization?.name || '',
      },
      websiteUrl: hospital.website_url || '',
    }
  },
)

export const formatBabyGrowthChartResult = wrapFormatFn(
  (data: ResultApi): Result => {
    if (!data) return {} as Result
    return {
      conditionRiskSuggestion: data?.condition_risk_suggestion,
      faqs: data?.faqs,
      recommendArticles: data?.recommend_articles?.map(formatArticle) || [],
      seeMoreLink: data?.seemore_link,
    }
  },
)

export const formatBabyGrowthRisk = wrapFormatFn((data: any) => {
  if (!data) return {}

  return {
    '3rd': formatBabyGrowthChartResult(data['3rd']),
    '15th': formatBabyGrowthChartResult(data['15th']),
    '50th': formatBabyGrowthChartResult(data['50th']),
    '85th': formatBabyGrowthChartResult(data['85th']),
    '97th': formatBabyGrowthChartResult(data['97th']),
  }
})
export const formatBabyGrowthResult = wrapFormatFn(
  (data: BabyGrowthResultApi): BabyGrowthResult => {
    if (!data) return {} as BabyGrowthResult
    return ({
      boy_head: formatBabyGrowthRisk(data?.boy_head),
      boy_height: formatBabyGrowthRisk(data?.boy_height),
      boy_weight: formatBabyGrowthRisk(data?.boy_weight),
      girl_head: formatBabyGrowthRisk(data?.girl_head),
      girl_height: formatBabyGrowthRisk(data?.girl_height),
      girl_weight: formatBabyGrowthRisk(data?.girl_weight),
      generalSetting: {
        community: data.general_setting?.community || null,
        disclaimer: data.general_setting?.disclaimer || null,
      },
    } as any) as BabyGrowthResult
  },
)

export const formatBabyPoopPage = wrapFormatFn(
  (data: BabyPoopPageApi): BabyPoopPage | null => {
    if (!data || !Object.keys(data).length) {
      return null
    }

    const _formatSponsorBanner = wrapFormatFn(
      (
        sponsorBannerApi: BabyPoopPageApi['sponsor_banner'],
      ): BabyPoopPage['sponsorBanner'] => {
        if (!sponsorBannerApi) {
          return null
        }

        return {
          desktopImage: sponsorBannerApi.desktop_image,
          mobileImage: sponsorBannerApi.mobile_image,
          showSponsorBanner: sponsorBannerApi.show_sponsor_banner,
          videoBlock: sponsorBannerApi.video_block
            ? {
                title: sponsorBannerApi.video_block.title,
                listVideo: (sponsorBannerApi.video_block.list_video || []).map(
                  (v) => {
                    return {
                      title: v.title,
                      videoUrl: v.video_url,
                    }
                  },
                ),
              }
            : null,
        }
      },
    )

    const _formatReviewer = wrapFormatFn(
      (
        reviewApi: BabyPoopPageApi['reviewed_by'],
      ): BabyPoopPage['reviewedBy'] => {
        if (!reviewApi) return null

        return {
          ID: reviewApi.ID,
          userId: reviewApi.user_id,
          formerRole: reviewApi.former_role,
          role: reviewApi.role,
          slug: reviewApi.slug,
          displayName: reviewApi.display_name,
          description: reviewApi.description,
          profileUrl: reviewApi.profile_url,
          avatar: reviewApi.avatar,
          avatarThumbnail: reviewApi.avatar_thumbnail,
          turnOffProfile: reviewApi.turn_off_profile,
          specialty: reviewApi.specialty,
          organisation: reviewApi.organisation,
          organisationUrl: reviewApi.organisation_url,
        }
      },
    )

    /**
     * Format result object into an Array
     * @param resultApi - result object returned by api
     * @returns an Array of formattedResultItem
     */
    const _formatResult = (
      resultApi: BabyPoopPageApi['result'],
    ): BabyPoopPage['result'] => {
      const formattedResult: BabyPoopPage['result'] = []

      for (const poopType in resultApi) {
        if (!resultApi.hasOwnProperty(poopType)) {
          continue
        }

        const item = resultApi[poopType]
        formattedResult.push({
          name: poopType,
          image: item.image || '',
          color: item.color || '',
          textInput: item.text_input || '',
          titleResult: item.title_result || '',
          description: item.description || '',
          whatShouldIDo: item.what_should_i_do || '',
          faqs: item.faqs || [],
          recommendArticles: (item.recommend_articles || []).map(formatArticle),
        })
      }

      return formattedResult
    }

    return {
      enable_ad: !!data?.enable_ad,
      englishName: data.english_name,
      featuredImage: data.featured_image,
      isSponsored: data.is_sponsored,
      sponsor: data.sponsor,
      sponsorBanner: _formatSponsorBanner(data.sponsor_banner),
      disclaimer: data.disclaimer,
      information: data.information,
      toolGuideline: data.tool_guideline,
      care: data.care,
      reviewedBy: _formatReviewer(data.reviewed_by),
      reviewedDate: data.reviewed_date || '',
      content: data.content,
      result: _formatResult(data.result),
      isLeadgen: Boolean(data.is_leadgen),
      metaTag: data.meta_tag,
      ...formatCommonHealthtoolForm(data),
    }
  },
)

export const formatBabyVaccinationContent = wrapFormatFn(
  (data: BabyVaccinationContentApi): BabyVaccinationContent => {
    if (!data) return {} as BabyVaccinationContent

    return {
      enable_ad: !!data?.enable_ad,
      information:
        data.info?.map?.((info) => ({
          title: info?.title,
          description: info?.description,
        })) ?? [],
      reviewBy: {
        avatar: data.reviewed_by?.avatar_thumbnail || '',
        reviewer:
          data.reviewed_by?.display_name || data.reviewed_by?.name || '',
        date: data?.reviewed_date || '',
        link: formatUserProfileUrl(data?.reviewed_by),
      },
      metaTag: data?.meta_tag || '',
      disclaimer: data?.disclaimer || '',
      is_sponsored: data?.is_sponsored || false,
      sponsor: data?.sponsor || {},
      featuredImage: data?.featured_image || '',
      isLeadgen: Boolean(data.is_leadgen),
      pregnancyNavigation: data?.pregnacy_navigation || null,
      ...formatCommonHealthtoolForm(data),
    }
  },
)

export const formatAgeGroup = wrapFormatFn(
  (ageGroup: AgeGroupApi): AgeGroup => {
    if (!ageGroup) return {} as AgeGroup
    return {
      id: ageGroup.id,
      name: ageGroup.name,
      parent: ageGroup.parent,
      shortName: ageGroup.short_name,
      fromDay: ageGroup.from_day ? Number(ageGroup.from_day) : null,
      toDay: ageGroup.to_day ? Number(ageGroup.to_day) : null,
    }
  },
)
export const formatBabyVaccinationResult = wrapFormatFn(
  (data: BabyVaccinationResultApi): BabyVaccinationResult => {
    if (!data) return {} as BabyVaccinationResult
    const ageGroups: AgeGroup[] = data.banner?.age_group
      ? data.banner?.age_group?.map(formatAgeGroup)
      : []

    return {
      enable_ad: !!data?.enable_ad,
      seeMoreLink: data.see_more_link || '',
      faqs: data.faqs,
      banner: {
        title: data.banner?.title || '',
        boyBanner: data.banner?.boy_banner || '',
        girlBanner: data.banner?.girl_banner || '',
        readMoreUrl: data.banner?.read_more_url || '',
        ageGroup: ageGroups,
      },
      suggestedArticles: data?.suggested_articles?.map(formatArticle) ?? [],
      communityTitle: data.community_title || '',
      community: formatCommunityEmbed(data.community),
      pregnancyNavigation: data.pregnacy_navigation || null,
    }
  },
)

export const formatSpecialty = wrapFormatFn(
  (data: SpecialtyApi): Specialty => {
    const { count_experts, term_id, ...rest } = data
    return {
      ...rest,
      countExperts: count_experts,
      termId: term_id,
    }
  },
)
